import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useCrewmates } from '@/hooks/useCrewmates';
import { useFollowing } from '@/hooks/useFollowing';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';
import { MessageCircle, Search, Users, UserCheck, ArrowRight } from 'lucide-react';

interface NewMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Candidate {
  pubkey: string;
  /** Short context line, e.g. shared crews or "Following". */
  hint?: string;
  /** Grouping bucket for section headers. */
  group: 'crew' | 'following';
}

/** A single selectable person row. Resolves its own profile for name/avatar. */
function PersonRow({
  candidate,
  query,
  onPick,
}: {
  candidate: Candidate;
  query: string;
  onPick: (pubkey: string) => void;
}) {
  const author = useAuthor(candidate.pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? genUserName(candidate.pubkey);
  const nip05 = metadata?.nip05;

  // Name-based filtering happens here (names are resolved async per row).
  const q = query.trim().toLowerCase();
  if (q) {
    const haystack = `${name} ${nip05 ?? ''} ${candidate.hint ?? ''}`.toLowerCase();
    if (!haystack.includes(q)) return null;
  }

  return (
    <button
      type="button"
      onClick={() => onPick(candidate.pubkey)}
      className="flex w-full items-center gap-3 rounded-lg p-2.5 text-left transition-colors hover:bg-gray-800 active:scale-[0.99]"
    >
      <Avatar className="h-10 w-10 border-2 border-gray-700 flex-shrink-0">
        <AvatarImage src={metadata?.picture} alt={name} />
        <AvatarFallback className="bg-[#ff0040]/20 text-[#ff0040] text-xs">
          {name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-white text-sm truncate">{name}</p>
        {candidate.hint && (
          <p className="text-xs text-gray-500 truncate">{candidate.hint}</p>
        )}
      </div>
      <ArrowRight className="h-4 w-4 text-gray-600 flex-shrink-0" />
    </button>
  );
}

/**
 * A best-in-class "New Message" picker: search your crew-mates and the people
 * you follow by name, or paste a raw npub / hex key. Replaces the old
 * paste-only flow so starting a DM feels like a real contacts screen.
 */
export function NewMessageDialog({ open, onOpenChange }: NewMessageDialogProps) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { crewmates, isLoading: crewmatesLoading } = useCrewmates();
  const { data: following, isLoading: followingLoading } = useFollowing(user?.pubkey);

  const [query, setQuery] = useState('');
  const [rawError, setRawError] = useState('');

  // Merge crew-mates (with shared-crew hints) and followed accounts, de-duped.
  const candidates = useMemo<Candidate[]>(() => {
    const seen = new Set<string>();
    const list: Candidate[] = [];

    for (const cm of crewmates) {
      if (seen.has(cm.pubkey)) continue;
      seen.add(cm.pubkey);
      list.push({
        pubkey: cm.pubkey,
        group: 'crew',
        hint:
          cm.sharedCrews.length > 0
            ? `In your crew${cm.sharedCrews.length > 1 ? 's' : ''}: ${cm.sharedCrews.join(', ')}`
            : 'Crew-mate',
      });
    }

    for (const pk of following ?? []) {
      if (pk === user?.pubkey || seen.has(pk)) continue;
      seen.add(pk);
      list.push({ pubkey: pk, group: 'following', hint: 'Following' });
    }

    return list;
  }, [crewmates, following, user?.pubkey]);

  const crewCandidates = candidates.filter((c) => c.group === 'crew');
  const followingCandidates = candidates.filter((c) => c.group === 'following');

  const goToConversation = (pubkey: string) => {
    onOpenChange(false);
    setQuery('');
    setRawError('');
    navigate(`/messages/${nip19.npubEncode(pubkey)}`);
  };

  // If the query looks like a raw key, offer to open it directly.
  const trimmed = query.trim();
  const looksLikeNpub = trimmed.startsWith('npub1');
  const looksLikeHex = /^[a-f0-9]{64}$/i.test(trimmed);

  const handleOpenRaw = () => {
    setRawError('');
    try {
      let pubkey = trimmed;
      if (looksLikeNpub) {
        const decoded = nip19.decode(trimmed);
        if (decoded.type !== 'npub') throw new Error('Invalid npub');
        pubkey = decoded.data;
      } else if (!looksLikeHex) {
        throw new Error('Invalid key');
      }
      goToConversation(pubkey);
    } catch {
      setRawError('That doesn\u2019t look like a valid npub or public key.');
    }
  };

  const isLoading = crewmatesLoading || followingLoading;
  const hasAnyCandidates = candidates.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-2 border-[#ff0040]/50 mx-4 max-w-[calc(100vw-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <MessageCircle className="h-5 w-5 text-[#ff0040]" />
            New Message
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">
            Search your people by name, or paste an npub to message anyone.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setRawError('');
              }}
              placeholder="Search name or paste npub1..."
              className="bg-gray-900 border-gray-700 text-white pl-9 h-11"
              autoFocus
            />
          </div>

          {/* Direct-open affordance when a raw key is pasted */}
          {(looksLikeNpub || looksLikeHex) && (
            <Button
              onClick={handleOpenRaw}
              className="w-full bg-[#ff0040] hover:bg-[#ff0040]/90 text-white font-bold h-11"
            >
              Message this key
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
          {rawError && <p className="text-sm text-red-500">{rawError}</p>}

          {/* People list */}
          {!looksLikeNpub && !looksLikeHex && (
            <ScrollArea className="h-[min(50vh,360px)] pr-2">
              {isLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  Loading your people...
                </div>
              ) : !hasAnyCandidates ? (
                <div className="py-10 text-center px-4">
                  <Users className="h-10 w-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-sm text-gray-400">
                    No crew-mates or follows yet.
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Paste someone&apos;s npub above to start a conversation.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {crewCandidates.length > 0 && (
                    <div>
                      <p className="flex items-center gap-1.5 px-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-teal-400">
                        <Users className="h-3.5 w-3.5" /> Your crews
                      </p>
                      <div className="space-y-1">
                        {crewCandidates.map((c) => (
                          <PersonRow
                            key={c.pubkey}
                            candidate={c}
                            query={query}
                            onPick={goToConversation}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {followingCandidates.length > 0 && (
                    <div>
                      <p className="flex items-center gap-1.5 px-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                        <UserCheck className="h-3.5 w-3.5" /> Following
                      </p>
                      <div className="space-y-1">
                        {followingCandidates.map((c) => (
                          <PersonRow
                            key={c.pubkey}
                            candidate={c}
                            query={query}
                            onPick={goToConversation}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
